import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Mail, GraduationCap, Trophy, Flame, Star, Award, Pencil, Save, Sparkles, BookOpen, School, Check } from 'lucide-react';
import { AvatarBuilder, AvatarSVG } from '../components/AvatarBuilder';
import { apiService } from '../services/api';
import { toast } from 'sonner';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, updateUser } = useAuth();
  const [editingAvatar, setEditingAvatar] = useState(false);
  const [editingTutor, setEditingTutor] = useState(false);
  const [avatarData, setAvatarData] = useState({
    skin: user?.avatar_skin || '#FFD5A0',
    hair: user?.avatar_hair || '#4A3728',
    eyes: user?.avatar_eyes || '#4D96FF',
    accessory: user?.avatar_accessory || 'none',
  });
  const [tutorName, setTutorName] = useState(user?.tutor_name || 'Buddy');
  const [saving, setSaving] = useState(false);
  const [classCode, setClassCode] = useState('');
  const [joining, setJoining] = useState(false);

  const joinClass = async () => {
    if (!classCode.trim()) { toast.error('Enter invite code'); return; }
    setJoining(true);
    try {
      const res = await apiService.joinClass({ invite_code: classCode.toUpperCase() });
      updateUser({ class_id: res.data.class_name, class_name: res.data.class_name });
      toast.success(res.data.message);
      setClassCode('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code');
    } finally { setJoining(false); }
  };

  const saveAvatar = async () => {
    setSaving(true);
    try {
      const res = await apiService.updateAvatar({
        avatar_skin: avatarData.skin,
        avatar_hair: avatarData.hair,
        avatar_eyes: avatarData.eyes,
        avatar_accessory: avatarData.accessory,
      });
      updateUser(res.data.user);
      toast.success('Avatar updated!');
      setEditingAvatar(false);
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const saveTutorName = async () => {
    setSaving(true);
    try {
      const res = await apiService.updateAvatar({ tutor_name: tutorName });
      updateUser(res.data.user);
      toast.success(`Tutor renamed to ${tutorName}!`);
      setEditingTutor(false);
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const badges = [
    { id: 'week_warrior', name: 'Week Warrior', icon: '🔥', description: '7 day streak' },
    { id: 'century_club', name: 'Century Club', icon: '💯', description: '100 questions answered' },
    { id: 'speed_demon', name: 'Speed Demon', icon: '⚡', description: 'Answer in under 10 seconds' },
    { id: 'perfect_score', name: 'Perfect Score', icon: '🎯', description: '100% accuracy in a session' },
    { id: 'early_bird', name: 'Early Bird', icon: '🌅', description: 'Practice before 8 AM' },
    { id: 'night_owl', name: 'Night Owl', icon: '🦉', description: 'Practice after 10 PM' },
  ];
  const userBadges = user?.badges || [];

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="profile-page">
      <div className="bg-white border-b-2 border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button data-testid="back-to-dashboard-btn" onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-[#64748B] hover:text-[#1E293B] font-semibold mb-4">
            <ArrowLeft className="w-5 h-5" />Back to Dashboard
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Card with Avatar */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-8">
          <div className="flex items-start gap-6">
            <div className="flex flex-col items-center gap-2">
              <AvatarSVG skin={avatarData.skin} hair={avatarData.hair} eyes={avatarData.eyes} accessory={avatarData.accessory} size={120} />
              <button onClick={() => setEditingAvatar(!editingAvatar)} data-testid="edit-avatar-btn"
                className="flex items-center gap-1 text-sm font-bold text-[#4D96FF] hover:underline">
                <Pencil className="w-3 h-3" /> Edit Avatar
              </button>
            </div>
            <div className="flex-1">
              <h1 className="text-4xl font-black text-[#1E293B] mb-2">{user?.username}</h1>
              <div className="flex items-center gap-6 text-[#64748B] flex-wrap">
                <div className="flex items-center gap-2"><Mail className="w-5 h-5" /><span className="font-semibold">{user?.email}</span></div>
                <div className="flex items-center gap-2"><GraduationCap className="w-5 h-5" /><span className="font-semibold">Grade {user?.grade_level}</span></div>
              </div>
            </div>
          </div>

          {/* Avatar Editor */}
          {editingAvatar && (
            <div className="mt-6 pt-6 border-t-2 border-slate-100">
              <h3 className="text-xl font-bold text-[#1E293B] mb-4">Customize Your Avatar</h3>
              <AvatarBuilder skin={avatarData.skin} hair={avatarData.hair} eyes={avatarData.eyes} accessory={avatarData.accessory}
                onChange={(updates) => setAvatarData(prev => ({ ...prev, ...updates }))} />
              <button onClick={saveAvatar} disabled={saving} data-testid="save-avatar-btn"
                className="mt-4 px-8 py-3 bg-[#38E54D] text-white rounded-2xl font-bold border-b-4 border-green-600 active:border-b-0 active:translate-y-1 transition-all flex items-center gap-2 disabled:opacity-50">
                <Save className="w-5 h-5" /> {saving ? 'Saving...' : 'Save Avatar'}
              </button>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-6 border-t-2 border-slate-100">
            <StatItem icon={<Trophy className="w-6 h-6" />} label="Level" value={user?.level || 1} color="text-[#FFC03D]" />
            <StatItem icon={<Flame className="w-6 h-6" />} label="Streak" value={`${user?.current_streak || 0} days`} color="text-orange-500" />
            <StatItem icon={<Star className="w-6 h-6" />} label="Elo Rating" value={Math.round(user?.overall_elo_rating || 1500)} color="text-[#4D96FF]" />
            <StatItem icon={<Award className="w-6 h-6" />} label="Badges" value={userBadges.length} color="text-[#A493FF]" />
          </div>
        </div>

        {/* AI Tutor Name */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-black text-[#1E293B] flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-[#FFC03D]" /> Your AI Tutor
            </h2>
            <button onClick={() => setEditingTutor(!editingTutor)} className="text-sm font-bold text-[#4D96FF] hover:underline flex items-center gap-1">
              <Pencil className="w-3 h-3" /> Rename
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-[#FFC03D] to-orange-500 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <div>
              <p className="text-3xl font-black text-[#FFC03D]">{user?.tutor_name || 'Buddy'}</p>
              <p className="text-[#64748B]">Your personal AI learning companion</p>
            </div>
          </div>
        </div>

        {/* Class / School */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-8" data-testid="class-section">
          <h2 className="text-2xl font-black text-[#1E293B] flex items-center gap-3 mb-4">
            <School className="w-7 h-7 text-indigo-500" />My Class
          </h2>
          {user?.class_name ? (
            <div className="flex items-center gap-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 border-2 border-indigo-200">
              <Check className="w-10 h-10 text-green-600 flex-shrink-0 bg-green-100 rounded-full p-2" />
              <div>
                <p className="font-black text-lg text-[#1E293B]" data-testid="current-class-name">{user.class_name}</p>
                <p className="text-sm text-[#64748B]">You're enrolled in this class. Your teacher can see your progress.</p>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-[#64748B] mb-3">Ask your teacher for the class invite code (6 characters).</p>
              <div className="flex gap-2">
                <input type="text" value={classCode} onChange={e => setClassCode(e.target.value.toUpperCase())} maxLength={8}
                  placeholder="ABC123" data-testid="class-code-input"
                  className="flex-1 p-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-bold font-mono tracking-widest text-center text-lg uppercase focus:border-indigo-500 outline-none" />
                <button onClick={joinClass} disabled={joining} data-testid="join-class-submit"
                  className="px-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-bold disabled:opacity-50">
                  {joining ? '...' : 'Join'}
                </button>
              </div>
            </div>
          )}
          {editingTutor && (
            <div className="mt-4 flex gap-3">
              <input type="text" value={tutorName} onChange={(e) => setTutorName(e.target.value)} maxLength={20}
                className="flex-1 p-3 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl focus:border-[#FFC03D] outline-none font-bold text-lg" data-testid="rename-tutor-input" />
              <button onClick={saveTutorName} disabled={saving} data-testid="save-tutor-btn"
                className="px-6 py-3 bg-[#FFC03D] text-white rounded-2xl font-bold border-b-4 border-[#E6A820] active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>

        {/* Books Selection */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-8">
          <h2 className="text-2xl font-black text-[#1E293B] mb-2 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-[#A493FF]" /> My Books
          </h2>
          <p className="text-[#64748B] mb-4">Select which books you study. This personalizes your learning experience.</p>
          <BookSelector user={user} updateUser={updateUser} />
        </div>

        {/* Badges */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl mb-8">
          <h2 className="text-3xl font-black text-[#1E293B] mb-6 flex items-center gap-3"><Award className="w-8 h-8 text-[#FFC03D]" />Badge Collection</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {badges.map((badge) => {
              const isEarned = userBadges.includes(badge.id);
              return (
                <div key={badge.id} data-testid={`badge-${badge.id}`}
                  className={`p-6 rounded-2xl border-2 transition-all ${isEarned ? 'bg-gradient-to-br from-[#FFC03D] to-orange-500 border-[#FFC03D] text-white shadow-lg' : 'bg-slate-50 border-slate-200 text-[#64748B] opacity-50'}`}>
                  <div className="text-5xl mb-3 text-center">{badge.icon}</div>
                  <h3 className={`text-lg font-bold text-center mb-1 ${isEarned ? 'text-white' : 'text-[#1E293B]'}`}>{badge.name}</h3>
                  <p className={`text-sm text-center ${isEarned ? 'text-white/90' : 'text-[#64748B]'}`}>{badge.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Logout */}
        <div className="bg-white rounded-3xl p-8 border-2 border-slate-100 shadow-xl">
          <button data-testid="logout-btn" onClick={() => { logout(); navigate('/'); }}
            className="w-full px-6 py-4 bg-red-500 hover:bg-red-600 text-white rounded-2xl font-bold border-b-4 border-red-600 active:border-b-0 active:translate-y-1 transition-all">
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

const StatItem = ({ icon, label, value, color }) => (
  <div className="text-center">
    <div className={`${color} mb-2 flex justify-center`}>{icon}</div>
    <p className="text-sm text-[#64748B] font-semibold mb-1">{label}</p>
    <p className="text-2xl font-black text-[#1E293B]">{value}</p>
  </div>
);

const ALL_BOOKS = [
  { id: 'honeysuckle', name: 'Honeysuckle', grade: 6, subject: 'English', color: 'bg-pink-100 border-pink-300 text-pink-700' },
  { id: 'honeycomb', name: 'Honeycomb', grade: 7, subject: 'English', color: 'bg-amber-100 border-amber-300 text-amber-700' },
  { id: 'honeydew', name: 'Honeydew', grade: 8, subject: 'English', color: 'bg-sky-100 border-sky-300 text-sky-700' },
  { id: 'empowering_english', name: 'Empowering English', grade: 0, subject: 'English', color: 'bg-violet-100 border-violet-300 text-violet-700' },
  { id: 'compacta', name: 'Compacta', grade: 0, subject: 'English', color: 'bg-indigo-100 border-indigo-300 text-indigo-700' },
  { id: 'ganita_prakash', name: 'Ganita Prakash', grade: 0, subject: 'Mathematics', color: 'bg-blue-100 border-blue-300 text-blue-700' },
  { id: 'curiosity', name: 'Curiosity', grade: 0, subject: 'Science', color: 'bg-green-100 border-green-300 text-green-700' },
];

const BookSelector = ({ user, updateUser }) => {
  const [selectedBooks, setSelectedBooks] = useState(user?.selected_books || ['honeycomb', 'empowering_english', 'compacta', 'ganita_prakash', 'curiosity']);
  const [saving, setSaving] = useState(false);

  const toggleBook = (bookId) => {
    setSelectedBooks(prev => prev.includes(bookId) ? prev.filter(b => b !== bookId) : [...prev, bookId]);
  };

  const saveBooks = async () => {
    setSaving(true);
    try {
      const res = await apiService.updateAvatar({ selected_books: selectedBooks });
      updateUser(res.data.user);
      toast.success('Books updated!');
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        {ALL_BOOKS.map(book => {
          const isSelected = selectedBooks.includes(book.id);
          return (
            <button key={book.id} onClick={() => toggleBook(book.id)} data-testid={`book-${book.id}`}
              className={`p-4 rounded-2xl border-2 text-left transition-all ${isSelected ? `${book.color} ring-2 ring-offset-2 ring-blue-400` : 'bg-slate-50 border-slate-200 text-[#64748B] opacity-60'}`}>
              <p className="font-bold text-sm">{book.name}</p>
              <p className="text-xs opacity-75">{book.subject}{book.grade > 0 ? ` | Grade ${book.grade}` : ''}</p>
            </button>
          );
        })}
      </div>
      <button onClick={saveBooks} disabled={saving} data-testid="save-books-btn"
        className="px-6 py-3 bg-[#A493FF] text-white rounded-2xl font-bold border-b-4 border-purple-600 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50">
        {saving ? 'Saving...' : 'Save Book Selection'}
      </button>
    </div>
  );
};

export default ProfilePage;
